import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Link2, Hash, Mail, Copy, Trash2, Plus, Send, Eye, TrendingUp, Sparkles
} from 'lucide-react';

const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copied'); };

export default function MarketingExtrasPage() {
  const [tab, setTab] = useState('utm');

  return (
    <div className="space-y-6" data-testid="marketing-extras-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-600 flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Marketing Toolkit</h1>
          <p className="text-muted-foreground text-sm">UTM links, hashtag library, and email newsletters in one place.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="utm" data-testid="tab-utm"><Link2 className="h-4 w-4 mr-1.5" /> UTM Links</TabsTrigger>
          <TabsTrigger value="hashtags" data-testid="tab-hashtags"><Hash className="h-4 w-4 mr-1.5" /> Hashtag Library</TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="tab-newsletter"><Mail className="h-4 w-4 mr-1.5" /> Newsletter</TabsTrigger>
        </TabsList>

        <TabsContent value="utm"><UTMTab /></TabsContent>
        <TabsContent value="hashtags"><HashtagTab /></TabsContent>
        <TabsContent value="newsletter"><NewsletterTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ───────────────────────── UTM TAB ──────────────────────────────
function UTMTab() {
  const [links, setLinks] = useState([]);
  const [attribution, setAttribution] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', target_url: '', utm_source: 'meta', utm_medium: 'social', utm_campaign: '', utm_content: '', utm_term: '' });
  const [generated, setGenerated] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        apiClient.get('/marketing/utm'),
        apiClient.get('/marketing/utm/attribution'),
      ]);
      setLinks(r1.data?.links || []);
      setAttribution(r2.data?.attribution || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function gen() {
    if (!form.target_url || !form.utm_campaign) return toast.error('Target URL and Campaign are required');
    try {
      const r = await apiClient.post('/marketing/utm/generate', form);
      setGenerated(r.data);
      toast.success('UTM link generated');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  }
  async function del(id) {
    if (!window.confirm('Delete this link?')) return;
    try { await apiClient.delete(`/marketing/utm/${id}`); load(); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Generate Tracked Link</CardTitle><CardDescription>Issue a tagged URL → leads landing through it are auto-attributed to the campaign.</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Link name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. KSA Summer LP" data-testid="utm-name" /></div>
            <div><Label>Target URL *</Label><Input value={form.target_url} onChange={e => setForm({ ...form, target_url: e.target.value })} placeholder="https://clt-academy.com/courses" data-testid="utm-target" /></div>
            <div><Label>utm_source</Label><Input value={form.utm_source} onChange={e => setForm({ ...form, utm_source: e.target.value })} placeholder="meta, google, linkedin..." /></div>
            <div><Label>utm_medium</Label><Input value={form.utm_medium} onChange={e => setForm({ ...form, utm_medium: e.target.value })} placeholder="social, cpc, email..." /></div>
            <div><Label>utm_campaign *</Label><Input value={form.utm_campaign} onChange={e => setForm({ ...form, utm_campaign: e.target.value })} placeholder="summer_ksa_2026" data-testid="utm-campaign" /></div>
            <div><Label>utm_content</Label><Input value={form.utm_content} onChange={e => setForm({ ...form, utm_content: e.target.value })} placeholder="cta_button_1" /></div>
          </div>
          <Button onClick={gen} data-testid="utm-generate-btn"><Plus className="h-4 w-4 mr-1.5" />Generate</Button>
          {generated && (
            <div className="rounded-lg border bg-emerald-500/5 border-emerald-500/30 p-3 space-y-2">
              <p className="text-xs text-emerald-700 font-medium">Ready to share:</p>
              <div className="flex items-center gap-2">
                <code className="text-xs font-mono bg-card p-2 rounded break-all flex-1">{generated.final_url}</code>
                <Button size="sm" variant="outline" onClick={() => copy(generated.final_url)}><Copy className="h-3 w-3" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">All Tracked Links</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Campaign</TableHead><TableHead>Source/Medium</TableHead><TableHead>Leads</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {links.map(l => (
                <TableRow key={l.id} data-testid={`utm-row-${l.id}`}>
                  <TableCell className="text-sm">{l.name}</TableCell>
                  <TableCell className="text-xs font-mono">{l.utm_campaign}</TableCell>
                  <TableCell className="text-xs">{l.utm_source} / {l.utm_medium}</TableCell>
                  <TableCell className="text-xs"><Badge variant="outline">{l.leads_count} leads</Badge></TableCell>
                  <TableCell><div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => copy(l.final_url)}><Copy className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(l.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div></TableCell>
                </TableRow>
              ))}
              {links.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No tracked links yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4" />Campaign Attribution</CardTitle><CardDescription>Leads with utm_campaign set, grouped.</CardDescription></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Campaign</TableHead><TableHead className="text-right">Leads</TableHead><TableHead className="text-right">Enrolled</TableHead><TableHead className="text-right">Conv %</TableHead></TableRow></TableHeader>
            <TableBody>
              {attribution.map(a => (
                <TableRow key={a.campaign}>
                  <TableCell className="text-sm font-mono">{a.campaign}</TableCell>
                  <TableCell className="text-right">{a.leads}</TableCell>
                  <TableCell className="text-right text-emerald-700">{a.enrolled}</TableCell>
                  <TableCell className="text-right text-xs">{a.leads ? ((a.enrolled / a.leads) * 100).toFixed(1) : '0'}%</TableCell>
                </TableRow>
              ))}
              {attribution.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">No campaign-tagged leads yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ───────────────────────── HASHTAGS TAB ──────────────────────────
function HashtagTab() {
  const [groups, setGroups] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ tag: '', category: 'brand', description: '' });

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get('/marketing/hashtags');
      setGroups(r.data?.groups || {});
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function save() {
    if (!form.tag.trim()) return toast.error('Tag required');
    try {
      await apiClient.post('/marketing/hashtags', form);
      toast.success('Hashtag added');
      setOpen(false); setForm({ tag: '', category: 'brand', description: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  }
  async function del(id) {
    if (!window.confirm('Delete?')) return;
    try { await apiClient.delete(`/marketing/hashtags/${id}`); load(); } catch { toast.error('Failed'); }
  }

  function copyGroup(tags) {
    const text = tags.map(t => t.tag).join(' ');
    copy(text);
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <p className="text-sm text-muted-foreground">Group your hashtags. One-click copy the whole bundle for posts.</p>
        <Button onClick={() => setOpen(true)} data-testid="hashtag-add-btn"><Plus className="h-4 w-4 mr-1.5" />Add Hashtag</Button>
      </div>
      {Object.keys(groups).length === 0 && <Card><CardContent className="py-12 text-center text-muted-foreground">No hashtags yet.</CardContent></Card>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(groups).map(([cat, tags]) => (
          <Card key={cat}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base capitalize">{cat}</CardTitle>
                <CardDescription className="text-xs">{tags.length} tag{tags.length !== 1 ? 's' : ''}</CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={() => copyGroup(tags)}><Copy className="h-3 w-3 mr-1" />All</Button>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="flex flex-wrap gap-1.5">
                {tags.map(t => (
                  <Badge key={t.id} variant="outline" className="font-mono cursor-pointer group" onClick={() => copy(t.tag)} title={t.description || 'Click to copy'}>
                    {t.tag}
                    <Trash2 className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-70 text-red-500" onClick={(e) => { e.stopPropagation(); del(t.id); }} />
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="hashtag-modal">
          <DialogHeader><DialogTitle>New Hashtag</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Tag *</Label><Input placeholder="cltacademy (without #)" value={form.tag} onChange={e => setForm({ ...form, tag: e.target.value })} data-testid="hashtag-tag" /></div>
            <div><Label>Category</Label><Input list="cat-suggest" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} />
              <datalist id="cat-suggest"><option value="brand" /><option value="location" /><option value="course" /><option value="trending" /><option value="general" /></datalist>
            </div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="hashtag-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ───────────────────────── NEWSLETTER TAB ────────────────────────
function NewsletterTab() {
  const [form, setForm] = useState({ subject: '', html_body: '', audience: 'all_employees', custom_emails: '' });
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [viewDialog, setViewDialog] = useState({ open: false, campaign: null });

  const loadHistory = useCallback(async () => {
    try { const r = await apiClient.get('/marketing/newsletter/history'); setHistory(r.data?.campaigns || []); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  async function doPreview() {
    try {
      const r = await apiClient.post('/marketing/newsletter/preview', {
        audience: form.audience,
        custom_emails: form.audience === 'custom' ? form.custom_emails.split(',').map(s => s.trim()).filter(Boolean) : []
      });
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  }
  async function doSend() {
    if (!form.subject.trim() || !form.html_body.trim()) return toast.error('Subject and body required');
    if (!window.confirm(`Send to ${preview?.recipient_count ?? '?'} recipients?`)) return;
    setSending(true);
    try {
      const r = await apiClient.post('/marketing/newsletter/send', {
        subject: form.subject, html_body: form.html_body,
        audience: form.audience,
        custom_emails: form.audience === 'custom' ? form.custom_emails.split(',').map(s => s.trim()).filter(Boolean) : []
      });
      toast.success(`Sent: ${r.data.sent}, failed: ${r.data.failed}`);
      setForm({ subject: '', html_body: '', audience: 'all_employees', custom_emails: '' });
      setPreview(null);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2">
        <CardHeader className="pb-3"><CardTitle className="text-base">Compose Newsletter</CardTitle><CardDescription>Use <code className="text-xs">{`{{name}}`}</code> for personalisation.</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Subject *</Label><Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} data-testid="newsletter-subject" /></div>
          <div>
            <Label>Audience</Label>
            <Select value={form.audience} onValueChange={v => { setForm({ ...form, audience: v }); setPreview(null); }}>
              <SelectTrigger data-testid="newsletter-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all_employees">All active employees</SelectItem>
                <SelectItem value="all_leads">All active leads</SelectItem>
                <SelectItem value="warm_leads">Warm + hot leads</SelectItem>
                <SelectItem value="enrolled_leads">Enrolled students</SelectItem>
                <SelectItem value="custom">Custom (paste emails)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.audience === 'custom' && (
            <div><Label>Custom emails (comma-separated)</Label><Textarea rows={2} value={form.custom_emails} onChange={e => setForm({ ...form, custom_emails: e.target.value })} placeholder="a@x.com, b@x.com" /></div>
          )}
          <div><Label>HTML Body *</Label><Textarea rows={10} value={form.html_body} onChange={e => setForm({ ...form, html_body: e.target.value })} placeholder="Hi {{name}}, ..." className="font-mono text-xs" data-testid="newsletter-body" /></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={doPreview} data-testid="newsletter-preview"><Eye className="h-4 w-4 mr-1.5" />Preview Audience</Button>
            <Button onClick={doSend} disabled={sending || !preview} data-testid="newsletter-send">
              <Send className="h-4 w-4 mr-1.5" /> {sending ? 'Sending…' : `Send${preview ? ` (${preview.recipient_count})` : ''}`}
            </Button>
          </div>
          {preview && (
            <div className="rounded-lg border bg-card p-3 text-xs space-y-1">
              <p><strong>{preview.recipient_count}</strong> recipients</p>
              <p className="text-muted-foreground">Sample: {preview.sample?.slice(0, 6).join(', ')}{preview.sample?.length > 6 ? '…' : ''}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Send History</CardTitle></CardHeader>
        <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
          {history.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No campaigns sent yet.</p>}
          {history.map(c => (
            <button key={c.id} onClick={() => setViewDialog({ open: true, campaign: c })} className="block w-full text-left p-3 rounded-lg border hover:border-primary/40" data-testid={`campaign-${c.id}`}>
              <p className="font-medium text-sm truncate">{c.subject}</p>
              <p className="text-xs text-muted-foreground">{c.audience} • {new Date(c.created_at).toLocaleString()}</p>
              <div className="flex gap-1 mt-1">
                <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-700">{c.sent_count} sent</Badge>
                {c.failed_count > 0 && <Badge variant="outline" className="text-xs bg-red-500/10 text-red-700">{c.failed_count} failed</Badge>}
              </div>
            </button>
          ))}
        </CardContent>
      </Card>

      <Dialog open={viewDialog.open} onOpenChange={o => !o && setViewDialog({ open: false, campaign: null })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{viewDialog.campaign?.subject}</DialogTitle></DialogHeader>
          <div className="text-xs text-muted-foreground">Audience: {viewDialog.campaign?.audience} · Sent: {viewDialog.campaign?.sent_count} · Failed: {viewDialog.campaign?.failed_count}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
